import React, { useState, useRef, useEffect } from "react";
import { getApiUrl } from "../../config/api";
import { CloseIcon } from "./AdminIcons";
import { convertStarsToHtml, htmlToEditorText } from "../utils/helpers";
import MultiCategorySelector from "./MultiCategorySelector";
import DatePicker from "./DatePicker";

const CrudManager = ({
  title,
  items,
  setItems,
  user,
  icon,
  allUsers = [],
  setNotification,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [imageFiles, setImageFiles] = useState([]);
  const imageInputRef = useRef();
  const [viewingImage, setViewingImage] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // This effect specifically manages the document title when the modal is open.
  useEffect(() => {
    const originalPageTitle = document.title;
    const titleSuffix =
      " | Ahmedabad Post: અમદાવાદના તાજા ગુજરાતી સમાચાર, ગુજરાત અને દેશ-દુનિયા Live સમાચાર";

    if (isModalOpen) {
      if (currentItem) {
        const itemTitle = currentItem.title || "Item";
        document.title = `Edit: ${
          itemTitle.length > 30 ? itemTitle.substring(0, 30) + "..." : itemTitle
        }${titleSuffix}`;
      } else {
        document.title = `Add New Article${titleSuffix}`;
      }
    }

    return () => {
      if (isModalOpen) {
        document.title = originalPageTitle;
      }
    };
  }, [isModalOpen, currentItem]);

  const openModal = (item = null) => {
    setCurrentItem(item);
    const initialFormData = {
      categories: [],
      title: "",
      content: "",
      youtubeUrl: "",
      shortVideo: "",
      referenceUrl: "",
      embedUrl: "",
    };

    if (item) {
      let parsedCategories = [];
      if (typeof item.categories === "string") {
        try {
          parsedCategories = JSON.parse(item.categories);
        } catch (e) {
          console.error(
            "Failed to parse categories JSON string:",
            item.categories
          );
          parsedCategories = [item.categories];
        }
      } else if (Array.isArray(item.categories)) {
        parsedCategories = item.categories;
      }

      const mappedData = {
        ...item,
        categories: parsedCategories,
        shortVideo: item.shortVideo || item.shortsVideo || "",
        referenceUrl: item.reference_url || "",
        embedUrl: item.embedUrl || item.embed_url || "",
        content: htmlToEditorText(item.content || ""),
      };

      setFormData(mappedData);

      const currentImages = Array.isArray(item.image)
        ? item.image.filter((img) => img).map((img) => img.replace(/"/g, ""))
        : [];
      setImageFiles(currentImages);
    } else {
      setFormData(initialFormData);
      setImageFiles([]);
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
    setImageFiles([]);
  };

  const handleCategoryChange = (categoryValue) => {
    setFormData((prev) => {
      const currentCategories = prev.categories || [];
      const newCategories = currentCategories.includes(categoryValue)
        ? currentCategories.filter((c) => c !== categoryValue)
        : [...currentCategories, categoryValue];
      return { ...prev, categories: newCategories };
    });
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "images") {
      const selectedFiles = Array.from(files);
      setImageFiles((prev) => {
        const existingFiles = prev.filter((f) => typeof f === "string" || f instanceof File);
        const allFiles = [...existingFiles, ...selectedFiles];
        const uniqueFiles = [];
        const fileMap = new Map();
        allFiles.forEach((file) => {
          if (typeof file === "string") {
            if (!fileMap.has(file)) {
              fileMap.set(file, true);
              uniqueFiles.push(file);
            }
          } else if (file instanceof File) {
            const key = file.name + file.size;
            if (!fileMap.has(key)) {
              fileMap.set(key, true);
              uniqueFiles.push(file);
            }
          }
        });
        return uniqueFiles;
      });
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    } else if (name === "pdfFile") {
      setFormData((prev) => ({
        ...prev,
        pdfFile: files && files[0] ? files[0] : null,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const [contentPreview, setContentPreview] = useState("");

  useEffect(() => {
    setContentPreview(convertStarsToHtml(formData.content || ""));
  }, [formData.content]);

  const handleRemoveImage = (idx) => {
    const fileToRemove = imageFiles[idx];
    const updatedFiles = imageFiles.filter((_, i) => i !== idx);
    setImageFiles(updatedFiles);

    if (fileToRemove instanceof File && imageInputRef.current) {
      const dataTransfer = new DataTransfer();
      updatedFiles.forEach((file) => {
        if (file instanceof File) {
          dataTransfer.items.add(file);
        }
      });
      imageInputRef.current.files = dataTransfer.files;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    const formDataToSend = new FormData();

    Object.keys(formData).forEach((key) => {
      if (
        ![
          "image",
          "pdf",
          "pdfFile",
          "categories",
          "createdAt",
          "updatedAt",
          "id",
          "slug",
          "reference_url",
          "embed_url",
          "shortsVideo",
        ].includes(key)
      ) {
        if (key === "content") {
          const raw = formData.content || "";
          const contentHtml = /<[^>]+>/g.test(raw)
            ? raw
            : convertStarsToHtml(raw);
          formDataToSend.append("content", contentHtml);
        } else if (key === "referenceUrl") {
          formDataToSend.append("reference_url", formData[key] || "");
        } else if (key === "embedUrl") {
          formDataToSend.append("embedUrl", formData[key] || "");
        } else {
          formDataToSend.append(key, formData[key] || "");
        }
      }
    });

    if (formData.categories && Array.isArray(formData.categories)) {
      formDataToSend.append("categories", JSON.stringify(formData.categories));
    }

    imageFiles.forEach((file) => {
      if (file instanceof File) {
        formDataToSend.append("image", file);
      }
    });

    if (formData.pdfFile instanceof File) {
      formDataToSend.append("pdf", formData.pdfFile);
    }

    if (currentItem) {
      // UPDATE LOGIC
      try {
        const initialImageUrls = currentItem.image
          ? currentItem.image
              .filter((img) => img)
              .map((img) => img.replace(/"/g, ""))
          : [];
        const finalImageUrls = imageFiles.filter(
          (file) => typeof file === "string"
        );
        const deletedImages = initialImageUrls
          .filter((url) => !finalImageUrls.includes(url))
          .map((url) => url.split("/").pop());

        if (deletedImages.length > 0) {
          formDataToSend.append("deleteImages", JSON.stringify(deletedImages));
        }

        const res = await fetch(
          getApiUrl('UPDATE_NEWS', { id: currentItem.id }),
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
            body: formDataToSend,
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to update article.");
        }

        setItems(
          items.map((item) => (item.id === currentItem.id ? data.post : item))
        );
        setNotification({
          message: "Article updated successfully!",
          type: "success",
        });
        try {
          const channel = new BroadcastChannel('articles-update');
          channel.postMessage({ updated: true });
          channel.close();
        } catch (e) {
          console.error('BroadcastChannel is not supported in this browser.');
        }
        closeModal();
      } catch (error) {
        console.error("Update failed:", error);
        setNotification({ message: error.message, type: "error" });
      }
    } else {
      // CREATE LOGIC
      try {
        const res = await fetch(getApiUrl('CREATE_NEWS'), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formDataToSend,
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to create article.");
        }

        setItems((prevItems) => [data.post, ...prevItems]);
        setNotification({
          message: "Article created successfully!",
          type: "success",
        });
        try {
          const channel = new BroadcastChannel('articles-update');
          channel.postMessage({ updated: true });
          channel.close();
        } catch (e) {
          console.error('BroadcastChannel is not supported in this browser.');
        }
        closeModal();
      } catch (error) {
        console.error("Create failed:", error);
        setNotification({ message: error.message, type: "error" });
      }
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirmation(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    const id = deleteConfirmation;
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl('DELETE_NEWS', { id }), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to delete article.");
      }

      setItems(items.filter((item) => item.id !== id));
      setNotification({
        message: "Article deleted successfully!",
        type: "success",
      });
      try {
        const channel = new BroadcastChannel('articles-update');
        channel.postMessage({ updated: true });
        channel.close();
      } catch (e) {
        console.error('BroadcastChannel is not supported in this browser.');
      }
    } catch (error) {
      console.error("Delete failed:", error);
      setNotification({ message: error.message, type: "error" });
    } finally {
      setDeleteConfirmation(null);
    }
  };

  const canEdit = () => user.isAdmin === true;

  // Pagination logic
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = items.slice(startIndex, endIndex);

  const goToPage = (page) => {
    const pageNum = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(pageNum);
  };

  return (
    <>
      {isModalOpen ? (
        <div className="w-full bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6 pb-4 border-b">
            <h1 className="text-3xl font-bold text-gray-800">
              {currentItem ? "Edit" : "Add New"} {title.slice(0, -1)}
            </h1>
            <button
              onClick={closeModal}
              className="p-2 rounded-full hover:bg-gray-200 text-gray-600 hover:text-gray-900"
              aria-label="Go back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
              <div className="md:col-span-1">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="title"
                >
                  Title
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={formData.title || ""}
                  onChange={handleFormChange}
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div className="md:col-span-1">
                <MultiCategorySelector
                  selectedCategories={formData.categories || []}
                  onCategoryChange={handleCategoryChange}
                />
              </div>
              <div className="md:col-span-2">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="content"
                >
                  Content
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content || ""}
                  onChange={handleFormChange}
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  rows={8}
                  required
                />
                <div className="mt-3">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Preview</label>
                  <div
                    className="prose max-w-none p-3 border rounded bg-gray-50 text-gray-800 max-h-64 overflow-y-auto"
                    dangerouslySetInnerHTML={{
                      __html: (function() {
                        const raw = formData.content || "";
                        if (/<[^>]+>/g.test(raw)) return raw;
                        return convertStarsToHtml(raw);
                      })()
                    }}
                  />
                </div>
              </div>

              <div className="md:col-span-1">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="youtubeUrl"
                >
                  YouTube URL (Optional)
                </label>
                <input
                  id="youtubeUrl"
                  name="youtubeUrl"
                  type="url"
                  value={formData.youtubeUrl || ""}
                  onChange={handleFormChange}
                  placeholder="https://youtube.com/watch?v=..."
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="md:col-span-1">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="shortVideo"
                >
                  YouTube Shorts URL (Optional)
                </label>
                <input
                  id="shortVideo"
                  name="shortVideo"
                  type="url"
                  value={formData.shortVideo || ""}
                  onChange={handleFormChange}
                  placeholder="https://youtube.com/shorts/..."
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div className="md:col-span-1">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="referenceUrl"
                >
                  Reference URL
                </label>
                <input
                  id="referenceUrl"
                  name="referenceUrl"
                  type="url"
                  value={formData.referenceUrl || ""}
                  onChange={handleFormChange}
                  placeholder="https://example.com/..."
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div className="md:col-span-1">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="embedUrl"
                >
                  Embedded URL (Optional)
                </label>
                <input
                  id="embedUrl"
                  name="embedUrl"
                  type="url"
                  value={formData.embedUrl || ""}
                  onChange={handleFormChange}
                  placeholder="https://instagram.com/... or https://x.com/..."
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Images
                </label>
                <input
                  ref={imageInputRef}
                  type="file"
                  name="images"
                  multiple
                  accept="image/*"
                  onChange={handleFormChange}
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                {imageFiles.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    {imageFiles.length} file(s) selected
                  </p>
                )}

                {imageFiles.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                    {imageFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="relative group bg-gray-100 rounded overflow-hidden"
                      >
                        <img
                          src={
                            file instanceof File
                              ? URL.createObjectURL(file)
                              : file
                          }
                          alt={`Preview ${idx}`}
                          className="w-full h-24 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                        >
                          <span className="text-white font-bold text-sm">Remove</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  PDF File (Optional)
                </label>
                <input
                  type="file"
                  name="pdfFile"
                  accept=".pdf"
                  onChange={handleFormChange}
                  className="shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="md:col-span-2">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="publishedDate"
                >
                  Published Date (Optional)
                </label>
                <DatePicker
                  value={formData.publishedDate || ""}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, publishedDate: value }))
                  }
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 border-t pt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-medium disabled:opacity-50"
              >
                {currentItem ? "Update" : "Create"} {title.slice(0, -1)}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          {icon} {title}
        </h1>
        <button
          onClick={() => openModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-4 rounded-lg shadow text-sm transition-colors duration-300 w-full sm:w-auto"
        >
          Add New {title.slice(0, -1)}
        </button>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-md rounded-lg border border-gray-100">
        <div className="w-full overflow-x-auto">
          <table className="min-w-[900px] w-full table-fixed divide-y divide-gray-200 text-xs md:text-sm lg:text-base">
            <thead className="bg-red-700">
              <tr>
                <th
                  scope="col"
                  className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left font-medium text-white uppercase tracking-wider w-2/6 md:w-2/6 lg:w-2/6"
                >
                  Title
                </th>
                <th
                  scope="col"
                  className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left font-medium text-white uppercase tracking-wider w-1/6 md:w-1/6 lg:w-1/6"
                >
                  Category
                </th>
                <th
                  scope="col"
                  className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left font-medium text-white uppercase tracking-wider w-1/6 md:w-1/6 lg:w-1/6"
                >
                  Author
                </th>
                <th
                  scope="col"
                  className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left font-medium text-white uppercase tracking-wider w-1/6 md:w-1/6 lg:w-1/6"
                >
                  Published Date
                </th>
                <th
                  scope="col"
                  className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-center font-medium text-white uppercase tracking-wider w-1/6 md:w-1/6 lg:w-1/6"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-red-50 divide-y divide-gray-200">
              {paginatedItems.map((item) => (
                <tr key={item.id} className="hover:bg-red-100 transition-colors">
                  <td className="px-2 md:px-4 lg:px-6 py-2 md:py-4 font-medium text-gray-900 break-words max-w-[120px] md:max-w-[180px] lg:max-w-[240px] truncate">
                    <span title={item.title}>{item.title}</span>
                  </td>
                  <td className="px-2 md:px-4 lg:px-6 py-2 md:py-4 text-gray-500 break-words max-w-[80px] md:max-w-[120px] lg:max-w-[180px] truncate">
                    <span title={Array.isArray(item.categories) ? item.categories.join(', ') : JSON.parse(item.categories || '[]').join(', ')}>
                      {Array.isArray(item.categories)
                        ? item.categories.join(", ")
                        : JSON.parse(item.categories || "[]").join(", ")}
                    </span>
                  </td>
                  <td className="px-2 md:px-4 lg:px-6 py-2 md:py-4 text-blue-700 font-semibold max-w-[80px] md:max-w-[120px] lg:max-w-[180px] truncate">
                    <span title={allUsers.find((u) => u.id == item.userId)?.username || `ID: ${item.userId}`}>
                      {allUsers.find((u) => u.id == item.userId)?.username ||
                        `ID: ${item.userId}`}
                    </span>
                  </td>
                  <td className="px-2 md:px-4 lg:px-6 py-2 md:py-4 text-gray-500 max-w-[80px] md:max-w-[120px] lg:max-w-[180px] truncate">
                    <span title={item.publishedDate || item.date || new Date(item.createdAt).toLocaleString("en-US")}>
                      {item.publishedDate ? new Date(item.publishedDate).toLocaleDateString("en-GB") : (item.date ? item.date : new Date(item.createdAt).toLocaleDateString("en-GB"))}
                    </span>
                  </td>
                  
                  <td className="px-2 md:px-4 lg:px-6 py-2 md:py-4 font-medium">
                    {canEdit() ? (
                      <div className="flex items-center justify-center space-x-1 md:space-x-2">
                        <button
                          onClick={() => openModal(item)}
                          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-2 md:px-3 rounded-lg shadow transition-colors duration-200 text-xs md:text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-2 md:px-3 rounded-lg shadow transition-colors duration-200 text-xs md:text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Read-only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4">
        {paginatedItems.map((item) => (
          <div
            key={item.id}
            className="bg-white p-4 rounded-lg shadow-md border border-gray-100 flex flex-col space-y-3"
          >
            <div>
              <h3 className="font-bold text-gray-800 break-words">
                {item.title}
              </h3>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-semibold">Category: </span>
                {Array.isArray(item.categories)
                  ? item.categories.join(", ")
                  : JSON.parse(item.categories || "[]").join(", ")}
              </p>
              <p>
                <span className="font-semibold">Author: </span>
                <span className="text-blue-700">
                  {allUsers.find((u) => u.id == item.userId)?.username ||
                    `ID: ${item.userId}`}
                </span>
              </p>
              <p>
                <span className="font-semibold">Published: </span>
                {item.publishedDate ? new Date(item.publishedDate).toLocaleString("en-US", { year: '2-digit', month: '2-digit', day: '2-digit' }) : (item.date ? item.date : new Date(item.createdAt).toLocaleString("en-US", { year: '2-digit', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric', hour12: true }))}
              </p>
            </div>
            {canEdit() && (
              <div className="flex items-center justify-end space-x-3 border-t pt-3 mt-2">
                <button
                  onClick={() => openModal(item)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-1.5 px-4 rounded-lg shadow transition-colors duration-200 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold py-1.5 px-4 rounded-lg shadow transition-colors duration-200 text-sm"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {items.length > itemsPerPage && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow-md">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{startIndex + 1}</span> to <span className="font-semibold">{Math.min(endIndex, items.length)}</span> of <span className="font-semibold">{items.length}</span> items
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => {
                const page = i + 1;
                const isVisible =
                  page === 1 ||
                  page === totalPages ||
                  Math.abs(page - currentPage) <= 1;

                if (!isVisible && page > 1 && page < totalPages) {
                  if (page === 2 || page === totalPages - 1) {
                    return null;
                  }
                  if (
                    (page === 3 && currentPage > 3) ||
                    (page === totalPages - 2 && currentPage < totalPages - 2)
                  ) {
                    return <span key={`ellipsis-${page}`} className="px-1">...</span>;
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-2 rounded transition-colors ${
                      currentPage === page
                        ? 'bg-red-700 text-white font-semibold'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                );
              }).filter(Boolean)}
            </div>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {viewingImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-[100] flex justify-center items-center p-4"
          onClick={() => setViewingImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-[101]"
            onClick={() => setViewingImage(null)}
          >
            <CloseIcon />
          </button>
          <img
            src={viewingImage}
            alt="Full size preview"
            className="max-w-full max-h-full rounded-lg shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[100] flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">
            <div className="p-6 text-center">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Confirm Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete this article? This action cannot
                be undone.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmation(null)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-6 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </>
  );
};

export default CrudManager;
